import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { accountsService } from "../../services/accounts.service";
import { extractApiErrorMessage } from "../../utils/apiError";
import { EMPTY_MAIL_ACCOUNT_FILTERS, filterMailAccounts } from "./mailAccountAdminHelpers";

export default function useMailAccountsAdmin({ enabled = false } = {}) {
  const [mailAccounts, setMailAccounts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_MAIL_ACCOUNT_FILTERS);
  const [selectedId, setSelectedId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const loadRequestedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || loadRequestedRef.current) return;
    loadRequestedRef.current = true;
    accountsService
      .listManageableMailAccounts()
      .then((data) => {
        if (mountedRef.current) setMailAccounts(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        if (!mountedRef.current) return;
        setMailAccounts([]);
        setLoadError(extractApiErrorMessage(error) || "No se pudieron cargar las casillas.");
      });
  }, [enabled]);

  const filteredMailAccounts = useMemo(() => filterMailAccounts(mailAccounts ?? [], filters), [mailAccounts, filters]);

  const selectedMailAccount = useMemo(
    () => (mailAccounts ?? []).find((mailAccount) => mailAccount.id === selectedId) ?? null,
    [mailAccounts, selectedId],
  );

  const openMailAccount = useCallback((mailAccountOrId) => {
    setSelectedId(typeof mailAccountOrId === "object" ? mailAccountOrId?.id ?? null : mailAccountOrId);
  }, []);

  const closeMailAccount = useCallback(() => setSelectedId(null), []);

  const updateSelectedMailAccount = useCallback(
    (updater) => {
      setMailAccounts((previous) =>
        (previous ?? []).map((mailAccount) => (mailAccount.id === selectedId ? updater(mailAccount) : mailAccount)),
      );
    },
    [selectedId],
  );

  const removeMailAccount = useCallback((mailAccountId) => {
    setMailAccounts((previous) => (previous ?? []).filter((mailAccount) => mailAccount.id !== mailAccountId));
    setSelectedId((current) => (current === mailAccountId ? null : current));
  }, []);

  const addMailAccount = useCallback((mailAccount) => {
    setMailAccounts((previous) => [...(previous ?? []), mailAccount]);
  }, []);

  const openCreate = useCallback(() => setIsCreating(true), []);
  const closeCreate = useCallback(() => setIsCreating(false), []);

  return {
    mailAccounts,
    filteredMailAccounts,
    loadError,
    filters,
    setFilters,
    selectedMailAccount,
    openMailAccount,
    closeMailAccount,
    updateSelectedMailAccount,
    removeMailAccount,
    addMailAccount,
    isCreating,
    openCreate,
    closeCreate,
  };
}
